/**
 * Camera Permissions and Media Utility
 * Handles camera permission status checking, media device availability,
 * and progressive fallback stream requests for mobile and desktop browsers.
 */

/**
 * Checks if the current context supports camera media devices (e.g. HTTPS or localhost)
 * @returns {boolean}
 */
export const isSecureCameraContext = () => {
  if (typeof window === "undefined") return false;
  return Boolean(
    window.isSecureContext ||
    window.location.protocol === "https:" ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  );
};

/**
 * Checks if getUserMedia is supported in the browser
 * @returns {boolean}
 */
export const isMediaDevicesSupported = () => {
  if (typeof navigator === "undefined") return false;
  return Boolean(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
};

/**
 * Queries current camera permission state
 * @returns {Promise<'granted' | 'denied' | 'prompt' | 'unsupported'>}
 */
export const checkCameraPermission = async () => {
  if (!isMediaDevicesSupported()) {
    return "unsupported";
  }

  try {
    if (navigator.permissions && navigator.permissions.query) {
      const result = await navigator.permissions.query({ name: "camera" });
      return result.state; // 'granted' | 'denied' | 'prompt'
    }
  } catch (err) {
    // Some browsers (e.g. Firefox / Safari) may throw on name: "camera" in permissions.query
    console.warn("navigator.permissions.query for camera not supported:", err);
  }

  return "prompt";
};

/**
 * Map error names to clear, actionable Indonesian messages
 * @param {Error} error 
 * @returns {string}
 */
export const getCameraErrorMessage = (error) => {
  if (!error) return "Terjadi kesalahan saat mengakses kamera.";

  const name = error.name || "";
  const message = error.message || "";

  if (name === "NotAllowedError" || name === "PermissionDeniedError" || message.includes("Permission denied")) {
    return "Izin akses kamera ditolak. Silakan berikan izin akses kamera pada pengaturan browser untuk melanjutkan.";
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return "Tidak ada perangkat kamera yang terdeteksi pada perangkat ini.";
  }
  if (name === "NotReadableError" || name === "TrackStartError") {
    return "Kamera sedang digunakan oleh aplikasi lain atau mengalami kendala teknis. Tutup aplikasi lain yang menggunakan kamera.";
  }
  if (name === "OverconstrainedError" || name === "ConstraintNotSatisfiedError") {
    return "Kamera tidak mendukung konfigurasi resolusi atau mode yang diminta.";
  }
  if (!isSecureCameraContext()) {
    return "Akses kamera memerlukan koneksi aman (HTTPS). Silakan akses aplikasi melalui HTTPS.";
  }

  return `Gagal mengakses kamera: ${message || name || "Kesalahan tidak diketahui"}`;
};

/**
 * Requests camera media stream with progressive constraint fallbacks
 * @param {MediaStreamConstraints['video']} customVideoConstraints
 * @returns {Promise<MediaStream>}
 */
export const requestCameraStream = async (customVideoConstraints = {}) => {
  if (!isMediaDevicesSupported()) {
    throw new Error(
      isSecureCameraContext()
        ? "Browser tidak mendukung akses kamera."
        : "Akses kamera memerlukan HTTPS."
    );
  }

  const constraintFallbacks = [
    // 1. High-spec preferred constraints (back camera with ideal resolution)
    {
      video: {
        facingMode: { ideal: "environment" },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        ...customVideoConstraints,
      },
    },
    // 2. Simplified facing mode constraint
    {
      video: customVideoConstraints?.deviceId
        ? { deviceId: customVideoConstraints.deviceId }
        : { facingMode: "environment" },
    },
    // 3. User / front camera constraint fallback
    {
      video: { facingMode: "user" },
    },
    // 4. Basic video stream fallback
    {
      video: true,
    },
  ];

  let lastError = null;

  for (const constraints of constraintFallbacks) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (stream && stream.getVideoTracks().length > 0) {
        return stream;
      }
    } catch (err) {
      lastError = err;
      // If user actively denied permission, don't keep trying fallback constraints
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        throw err;
      }
      console.warn("Failed with constraints:", constraints, err);
    }
  }

  throw lastError || new Error("Gagal memulai stream kamera.");
};
